import countries from "i18n-iso-countries";
import { GuestFormDataWithCompletion } from "../models/StaticDataModel";
import { fetchGuestById } from "../api/GuestService";
import { LovSource, LovSources } from "../config/tenant";

/**
 * True when {@code id} is a short positive Opera profile id (not a placeholder timestamp,
 * negative submission row, or an in-flight CREATE backed by {@code checkInSubmissionId}).
 */
export const isOperaGuestProfileId = (
	id?: number | null,
	checkInSubmissionId?: number | null
): boolean => {
	if (checkInSubmissionId != null || id == null || id < 0) return false;
	return id > 0 && String(id).length < 11;
};

export const isEmptyGuest = (g?: GuestFormDataWithCompletion | null) => {
	if (!g) return true;
	return !(
		g.firstName ||
		g.lastName ||
		g.emailInfo?.email ||
		g.telephoneInfo?.phoneNumber ||
		g.addressInfo?.addressLine ||
		g.addressInfo?.cityName ||
		g.addressInfo?.postalCode ||
		g.identificationInfo?.identification?.idNumber
	);
};

/**
 * After {@link normalizeTitlesLovMap}, `titles` is `{ Opera LOV value → Opera LOV value }`
 * (e.g. `{ "Mr": "Mr", "Prof.": "Prof." }`). A guest profile fetched from `GET /guests/{id}`
 * already carries the LOV value, but historical / free-text entries in Opera or wrong-case
 * input from the form may not — pick the canonical value via exact, case-insensitive, and
 * label-based matches.
 *
 * - When `titles` is missing/empty, returns `raw` unchanged (LOV not loaded yet).
 * - When `titles` is present: returns the canonical LOV value, or `""` if there is no match
 *   (do not send junk to Opera's `UpdateName`).
 */
export function normalizeGuestTitleToLovCode(
	stored: string | undefined,
	titles: Record<string, string> | undefined
): string {
	const raw = stored?.trim() ?? "";
	if (!raw) return "";
	if (!titles || Object.keys(titles).length === 0) {
		return raw;
	}
	if (Object.prototype.hasOwnProperty.call(titles, raw)) {
		return raw;
	}
	const codeCi = Object.keys(titles).find((c) => c.toLowerCase() === raw.toLowerCase());
	if (codeCi) return codeCi;
	const byLabel = Object.entries(titles).find(([, label]) => label === raw);
	if (byLabel) return byLabel[0];
	const byLabelCi = Object.entries(titles).find(
		([, label]) => label.toLowerCase() === raw.toLowerCase()
	);
	if (byLabelCi) return byLabelCi[0];
	return "";
}

export const getEmptyGuest = (): GuestFormDataWithCompletion => ({
	id: 0,
	title: "",
	firstName: "",
	middleName: "",
	lastName: "",
	gender: "",
	dateOfBirth: { day: null, month: null, year: null },
	placeOfBirth: "",
	nationality: "",
	telephoneInfo: {
		id: "",
		type: "",
		phoneNumber: "",
		phoneUseType: "",
		primaryInd: false,
	},
	emailInfo: {
		id: "",
		type: "",
		email: "",
	},
	addressInfo: {
		id: "",
		type: "",
		addressLine: "",
		cityName: "",
		postalCode: "",
		country: "",
	},
	identificationInfo: {
		id: "",
		type: "",
		identification: {
			idType: "",
			idNumber: "",
		},
	},
	newsletter: false,
	completed: false,
	updateUser: false,
});

/**
 * Per-field {@link LovSource} defaults, kept in lockstep with the backend's
 * {@code LovSourceConfig.defaults()}. Used when {@code chain-info} omits the {@code lovSources}
 * block (e.g. SPA built against a backend that predates V14) so the SPA falls back to the
 * pre-V14 hard-wired behaviour.
 */
export const LOV_SOURCE_DEFAULTS: LovSources = {
	nationality: "NATIONALITY",
	birthCountry: "COUNTRYCODES",
	addressCountry: "COUNTRYCODES",
};

/** Raw per-LOV maps fetched at login. Stored on {@code AppContext} state. */
export interface LovMaps {
	nationalities?: Record<string, string>;
	countries?: Record<string, string>;
}

const isNonEmpty = (m?: Record<string, string>): m is Record<string, string> =>
	!!m && Object.keys(m).length > 0;

/**
 * Resolves the LOV map the SPA should use for a given form field, given the chain's
 * per-field {@link LovSources} config (defaults applied when missing) and the raw LOV maps
 * fetched at login.
 *
 * <p>Built-in fallback: when the configured source returns an empty map but the
 * {@link LovSource.NATIONALITY} map is non-empty, falls back to {@code NATIONALITY} so the
 * pre-V14 "empty COUNTRYCODES → substitute nationalities" behaviour for the country dropdowns
 * is preserved without operators having to set the override explicitly. Logs at WARN once per
 * fallback to help diagnose mis-configured LOVs.
 */
export const getLovForField = (
	field: keyof LovSources,
	sources: LovSources | undefined,
	maps: LovMaps
): Record<string, string> => {
	const resolved: LovSource = (sources ?? LOV_SOURCE_DEFAULTS)[field];
	const primary = resolved === "NATIONALITY" ? maps.nationalities : maps.countries;
	if (isNonEmpty(primary)) {
		return primary;
	}
	// Fallback preserves pre-V14 LoginService behaviour: an empty COUNTRYCODES LOV
	// silently substitutes nationalities for country dropdowns. Without this guard, post-V14
	// chains that have never customised their LOV source would suddenly serve an empty country
	// dropdown for any property whose Opera COUNTRYCODES LOV is empty.
	if (resolved !== "NATIONALITY" && isNonEmpty(maps.nationalities)) {
		// eslint-disable-next-line no-console
		console.warn(
			`[online-checkin] LOV for field "${field}" (source=${resolved}) is empty; ` +
				`falling back to NATIONALITY map so the dropdown is not empty.`
		);
		return maps.nationalities;
	}
	return primary ?? {};
};

/**
 * Per-field live LOV maps the Proceed gate uses to recognise legitimate user picks. Each
 * field-keyed value is the resolved LOV (already routed through {@link getLovForField} by the
 * caller). Shape mirrors what {@link normalizeNationalitiesLovMap} /
 * {@link normalizeCountriesLovMap} produce ({@code {lovCode: lovCode}}) so we can look up with
 * the {@code in} operator.
 */
export interface MandatoryDataLovs {
	nationality?: Record<string, string>;
	birthCountry?: Record<string, string>;
	addressCountry?: Record<string, string>;
}

/**
 * Returns whether the guest carries every mandatory field for a check-in submission.
 *
 * Two operating modes:
 *
 * <ul>
 *   <li><b>Strict (lovs provided)</b>: each of {@code nationality}, {@code placeOfBirth}
 *       (submitted as {@code birthCountry.code}) and {@code addressInfo.country} must be a key
 *       in its respective per-field LOV map. Use at <i>gate</i> sites (Proceed button, routing
 *       guard) where we want to block any value that isn't drawn from Opera's per-property LOV.
 *       This catches manual paste / programmatic injection of bad values (e.g. the historical
 *       {@code nationality: "GIANCARLO"} that shipped to Opera and got rejected 148 times).</li>
 *   <li><b>Loose (lovs omitted)</b>: just checks that {@code nationality}, {@code placeOfBirth}
 *       and {@code addressInfo.country} are non-empty. Use at <i>derivation</i> sites that
 *       compute the {@code completed} checkmark for already-hydrated guests; their values came
 *       from Opera so they're valid LOV codes by construction. The loose check also handles the
 *       early-hydration race where LOVs haven't loaded yet.</li>
 * </ul>
 *
 * We deliberately do not validate against the hardcoded {@link NATIONALITIES_MAP} /
 * {@link COUNTRIES_MAP} at this gate, because Opera properties commonly extend their LOV with
 * non-ISO-standard codes (e.g. Jadranka's nationality LOV includes {@code BGL} for Bangladesh
 * — real ISO-3 is {@code BGD} — which is the only Bangladesh option Jadranka exposes; if we
 * validated against the hardcoded map we'd block a perfectly valid dropdown pick). The
 * hardcoded maps stay in use for the scanner mapper, which can't see the live LOV.
 *
 * Each {@code lovs.*} map is the per-field LOV already resolved via {@link getLovForField}.
 * Callers shouldn't pass raw {@code state.countries} / {@code state.nationalities} here — those
 * are the source maps, not the per-field resolved ones.
 */
export interface MandatoryDataOptions {
	/**
	 * When true (the default), the booker must have an email. Phone is never part of this gate.
	 * Pass false for every guest after slot 0.
	 */
	requireContact?: boolean;
}

/** True when the address is missing or only whitespace. */
export const isBlankEmail = (email: string | undefined | null): boolean =>
	!(email ?? "").trim();

export const hasAllMandatoryData = (
	guest: GuestFormDataWithCompletion,
	lovs?: MandatoryDataLovs,
	options?: MandatoryDataOptions
): boolean => {
	const {
		firstName,
		lastName,
		dateOfBirth,
		nationality,
		placeOfBirth,
		addressInfo,
		emailInfo,
		identificationInfo,
	} = guest;
	const requireContact = options?.requireContact !== false;
	const hasTravelDoc = identificationInfo?.identification?.idNumber;
	// Booker keeps the historical presence check (whitespace still counts as filled).
	// Other guests skip email entirely; the form treats whitespace as empty for them.
	const hasValidNationality = lovs?.nationality
		? !!nationality && nationality in lovs.nationality
		: !!nationality;
	const hasValidBirthCountry = lovs?.birthCountry
		? !!placeOfBirth && placeOfBirth in lovs.birthCountry
		: !!placeOfBirth;
	const hasValidAddressCountry = lovs?.addressCountry
		? !!addressInfo?.country && addressInfo.country in lovs.addressCountry
		: !!addressInfo?.country;

	return (
		!!firstName &&
		!!lastName &&
		!!dateOfBirth.day &&
		!!dateOfBirth.month &&
		!!dateOfBirth.year &&
		hasValidNationality &&
		hasValidBirthCountry &&
		hasValidAddressCountry &&
		(!requireContact || !!emailInfo?.email) &&
		!!hasTravelDoc
	);
};

/**
 * Slot 0 is the booker and still needs an email. Every later guest may omit email and phone.
 * Proceed and the later-step route guard both use this so the two screens cannot drift.
 */
export const guestsReadyToProceed = (
	guests: GuestFormDataWithCompletion[],
	lovs?: MandatoryDataLovs
): boolean =>
	guests.every((guest, index) =>
		hasAllMandatoryData(guest, lovs, { requireContact: index === 0 })
	);

export const wasGuestPreFilled = (
	guest: GuestFormDataWithCompletion
): boolean => {
	return !!guest.firstName || !!guest.lastName || !!guest.id;
};

export const enrichGuests = async (guests: GuestFormDataWithCompletion[]) => {
	const enrichedGuests: GuestFormDataWithCompletion[] = await Promise.all(
		guests.map(async (g, index) => {
			if (g.id && g.id > 0 && !g.checkInSubmissionId && g.firstName && g.lastName) {
				try {
					const full = await fetchGuestById(g.id.toString());
					// Derive completion from what Opera actually returned. Hardcoding false here
					// used to clear the checkmark on every refresh, even when the profile already
					// had every mandatory field.
					return {
						...full,
						completed: hasAllMandatoryData(full, undefined, {
							requireContact: index === 0,
						}),
					};
				} catch (err) {
					console.warn(`❌ Could not fetch full guest for ID ${g.id}`, err);
				}
			}
			return g;
		})
	);
	return enrichedGuests;
};

export const normalizeGenderIn = (g: any): "male" | "female" | "" => {
	const s = String(g ?? "")
		.trim()
		.toUpperCase();
	if (s === "F" || s === "FEMALE") return "female";
	if (s === "M" || s === "MALE") return "male";
	return "";
};

export const toApiGender = (g: "male" | "female" | "" | undefined | null) =>
	g === "female" ? "FEMALE" : g === "male" ? "MALE" : undefined;

export const NATIONALITIES_MAP: Record<string, string> = {
	ABW: "Aruban",
	AFG: "Afghan",
	AGO: "Angolan",
	AIA: "Anguillan",
	ALB: "Albanian",
	AND: "Andorran",
	ANT: "Netherlands Antillean",
	ARE: "Emirati",
	ARG: "Argentine",
	ARM: "Armenian",
	ASM: "American Samoan",
	ATA: "Antarctican",
	ATG: "Antiguan and Barbudan",
	AUS: "Australian",
	AUT: "Austrian",
	AZE: "Azerbaijani",
	BDI: "Burundian",
	BEL: "Belgian",
	BEN: "Beninese",
	BFA: "Burkinabe",
	BGD: "Bangladeshi",
	BGR: "Bulgarian",
	BHR: "Bahraini",
	BHS: "Bahamian",
	BIH: "Bosnian and Herzegovinian",
	BLR: "Belarusian",
	BLZ: "Belizean",
	BMU: "Bermudian",
	BOL: "Bolivian",
	BRA: "Brazilian",
	BRB: "Barbadian",
	BRN: "Bruneian",
	BTN: "Bhutanese",
	BWA: "Botswanan",
	CAF: "Central African",
	CAN: "Canadian",
	CHE: "Swiss",
	CHL: "Chilean",
	CHN: "Chinese",
	CIV: "Ivorian",
	CMR: "Cameroonian",
	COD: "Congolese (DRC)",
	COG: "Congolese",
	COL: "Colombian",
	COM: "Comorian",
	CPV: "Cape Verdean",
	CRI: "Costa Rican",
	CUB: "Cuban",
	CYM: "Caymanian",
	CYP: "Cypriot",
	CZE: "Czech",
	DEU: "German",
	DJI: "Djiboutian",
	DMA: "Dominican",
	DNK: "Danish",
	DOM: "Dominican",
	DZA: "Algerian",
	ECU: "Ecuadorian",
	EGY: "Egyptian",
	ERI: "Eritrean",
	ESP: "Spanish",
	EST: "Estonian",
	ETH: "Ethiopian",
	FIN: "Finnish",
	FJI: "Fijian",
	FLK: "Falklander",
	FRA: "French",
	FSM: "Micronesian",
	GAB: "Gabonese",
	GBR: "British",
	GEO: "Georgian",
	GHA: "Ghanaian",
	GIB: "Gibraltarian",
	GIN: "Guinean",
	GLP: "Guadeloupean",
	GMB: "Gambian",
	GNB: "Bissau-Guinean",
	GNQ: "Equatorial Guinean",
	GRC: "Greek",
	GRD: "Grenadian",
	GRL: "Greenlander",
	GTM: "Guatemalan",
	GUF: "French Guianese",
	GUM: "Guamanian",
	GUY: "Guyanese",
	HKG: "Hong Konger",
	HND: "Honduran",
	HRV: "Croatian",
	HTI: "Haitian",
	HUN: "Hungarian",
	IDN: "Indonesian",
	IND: "Indian",
	IRL: "Irish",
	IRN: "Iranian",
	IRQ: "Iraqi",
	ISL: "Icelander",
	ISR: "Israeli",
	ITA: "Italian",
	JAM: "Jamaican",
	JOR: "Jordanian",
	JPN: "Japanese",
	KAZ: "Kazakh",
	KEN: "Kenyan",
	KGZ: "Kyrgyz",
	KHM: "Cambodian",
	KIR: "Kiribati",
	KNA: "Saint Kitts and Nevis",
	KOR: "South Korean",
	KWT: "Kuwaiti",
	LAO: "Lao",
	LBN: "Lebanese",
	LBR: "Liberian",
	LBY: "Libyan",
	LCA: "Saint Lucian",
	LIE: "Liechtensteiner",
	LKA: "Sri Lankan",
	LSO: "Basotho",
	LTU: "Lithuanian",
	LUX: "Luxembourger",
	LVA: "Latvian",
	MAC: "Macanese",
	MAR: "Moroccan",
	MCO: "Monegasque",
	MDA: "Moldovan",
	MDG: "Malagasy",
	MDV: "Maldivian",
	MEX: "Mexican",
	MHL: "Marshallese",
	MKD: "Macedonian",
	MLI: "Malian",
	MLT: "Maltese",
	MMR: "Burmese",
	MNE: "Montenegrin",
	MNG: "Mongolian",
	MOZ: "Mozambican",
	MRT: "Mauritanian",
	MSR: "Montserratian",
	MTQ: "Martinican",
	MUS: "Mauritian",
	MWI: "Malawian",
	MYS: "Malaysian",
	NAM: "Namibian",
	NCL: "New Caledonian",
	NER: "Nigerien",
	NGA: "Nigerian",
	NIC: "Nicaraguan",
	NLD: "Dutch",
	NOR: "Norwegian",
	NPL: "Nepalese",
	NRU: "Nauruan",
	NZL: "New Zealander",
	OMN: "Omani",
	PAK: "Pakistani",
	PAN: "Panamanian",
	PER: "Peruvian",
	PHL: "Filipino",
	PLW: "Palauan",
	PNG: "Papua New Guinean",
	POL: "Polish",
	PRI: "Puerto Rican",
	PRK: "North Korean",
	PRT: "Portuguese",
	PRY: "Paraguayan",
	PSE: "Palestinian",
	PYF: "French Polynesian",
	QAT: "Qatari",
	ROU: "Romanian",
	RUS: "Russian",
	RWA: "Rwandan",
	SAU: "Saudi",
	SDN: "Sudanese",
	SEN: "Senegalese",
	SGP: "Singaporean",
	SLE: "Sierra Leonean",
	SLB: "Solomon Islander",
	SLV: "Salvadoran",
	SMR: "San Marinese",
	SOM: "Somali",
	SRB: "Serbian",
	STP: "Sao Tomean",
	SUR: "Surinamese",
	SVK: "Slovak",
	SVN: "Slovene",
	SWE: "Swedish",
	SWZ: "Swazi",
	SYC: "Seychellois",
	SYR: "Syrian",
	TCD: "Chadian",
	TGO: "Togolese",
	THA: "Thai",
	TJK: "Tajik",
	TKM: "Turkmen",
	TON: "Tongan",
	TTO: "Trinidadian and Tobagonian",
	TUN: "Tunisian",
	TUR: "Turkish",
	TUV: "Tuvaluan",
	TWN: "Taiwanese",
	TZA: "Tanzanian",
	UGA: "Ugandan",
	UKR: "Ukrainian",
	URY: "Uruguayan",
	USA: "American",
	UZB: "Uzbek",
	VCT: "Vincentian",
	VEN: "Venezuelan",
	VGB: "British Virgin Islander",
	VIR: "U.S. Virgin Islander",
	VNM: "Vietnamese",
	VUT: "Ni-Vanuatu",
	WLF: "Wallisian",
	WSM: "Samoan",
	XKX: "Kosovar",
	ZAF: "South African",
	ZMB: "Zambian",
	ZWE: "Zimbabwean",
	YYY: "Stateless Person",
};

// Example of processed countries
export const COUNTRIES_MAP: Record<string, string> = {
	AA: "Åland Islands",
	GG: "Guernsey",
	JY: "Jersey",
	KS: "Kosovo",
	ZY: "Saint Barthélemy",
	ZZ: "Saint Martin (French part)",
	AF: "Afghanistan",
	AL: "Albania",
	DZ: "Algeria",
	AS: "American Samoa",
	AD: "Andorra",
	AO: "Angola",
	AI: "Anguilla St. Kitts",
	AQ: "Antarctica",
	AG: "Antigua and Barbuda",
	AR: "Argentina",
	AM: "Armenia",
	AW: "Aruba",
	AU: "Australia",
	AT: "Austria",
	AZ: "Azerbaijan",
	BS: "Bahamas",
	BH: "Bahrain",
	BD: "Bangladesh",
	BB: "Barbados",
	BY: "Belarus",
	BE: "Belgium",
	BZ: "Belize",
	BJ: "Benin",
	BM: "Bermuda",
	BT: "Bhutan",
	BO: "Bolivia",
	BQ: "Bonaire Saba St. Eustatius",
	BA: "Bosnia Herzegovina",
	BW: "Botswana",
	BV: "Bouvet Island",
	BR: "Brazil",
	IO: "British Indian Ocean Territory",
	BN: "Brunei Darussalam",
	BG: "Bulgaria",
	BF: "Burkina Fasa",
	BI: "Burundi",
	KH: "Cambodia",
	CM: "Cameroon",
	CA: "Canada",
	CV: "Cape Verde",
	KY: "Cayman Islands",
	CF: "Central African Republic",
	TD: "Chad",
	CL: "Chile",
	CN: "China",
	CX: "Christmas Island",
	CC: "Cocos Keeling Islands",
	CO: "Colombia",
	KM: "Comoros",
	CG: "Congo",
	CD: "Congo the Democratic Republic of the",
	CK: "Cook Island",
	CR: "Costa Rica",
	CI: "Cote D Ivoire",
	HR: "Croatia",
	CU: "Cuba",
	CW: "Curacao",
	CY: "Cyprus",
	CZ: "Czech Republic",
	DK: "Denmark",
	DJ: "Djibouti",
	DM: "Dominica",
	DO: "Dominican Republic",
	TP: "East Timor",
	EC: "Ecuador",
	EG: "Egypt",
	SV: "El Salvador",
	GQ: "Equatorial Guinea",
	ER: "Eritrea",
	EE: "Estonia",
	ET: "Ethiopia",
	FK: "Falkland Islands",
	FO: "Faroe Islands",
	FJ: "Fiji",
	FI: "Finland",
	FR: "France",
	GF: "French Guiana",
	PF: "French Polynesia",
	TF: "French Souther Territories",
	GA: "Gabon",
	GM: "Gambia",
	GE: "Georgia",
	DE: "Germany",
	GH: "Ghana",
	GI: "Gibraltar",
	GB: "Great Britain",
	GR: "Greece",
	GL: "Greenland",
	GD: "Grenada",
	GP: "Guadeloupe St. Martin",
	GU: "Guam",
	GT: "Guatemala",
	GN: "Guinea",
	GW: "Guinea Bissau",
	GY: "Guyana",
	HT: "Haiti",
	HM: "Heard and Mcdonald Islands",
	HN: "Honduras",
	HK: "Hong Kong",
	HU: "Hungary",
	IS: "Iceland",
	IN: "India",
	ID: "Indonesia",
	IR: "Iran",
	IQ: "Iraq",
	IE: "Ireland",
	IM: "Isle of Man",
	IL: "Israel",
	IT: "Italy",
	JM: "Jamaica",
	JP: "Japan",
	JO: "Jordan",
	KZ: "Kazakhstan",
	KE: "Kenya",
	KI: "Kiribati",
	KP: "Korea Democratic People S Republic of",
	KR: "Korea Republic of",
	XK: "Kosovo",
	KW: "Kuwait",
	KG: "Kyrgzstan",
	LA: "Laos",
	LV: "Latvia",
	LB: "Lebanon",
	LS: "Lesotho",
	LR: "Liberia",
	LY: "Libyan Arab Jamahiriya",
	LI: "Liechtenstein",
	LT: "Lithuania",
	LU: "Luxembourg",
	MO: "Macau",
	MK: "Macedonia",
	MG: "Madagascar",
	MW: "Malawi",
	MY: "Malaysia",
	MV: "Maldives",
	ML: "Mali",
	MT: "Malta",
	MH: "Marshall Islands",
	MQ: "Martinique",
	MR: "Mauritania",
	MU: "Mauritius",
	YT: "Mayotte",
	MX: "Mexico",
	FM: "Micronesia Federated States of",
	MI: "Midway Islands",
	MD: "Moldova",
	MC: "Monaco",
	MN: "Mongolia",
	ME: "Montenegro",
	MS: "Montserrat",
	MA: "Morocco",
	MZ: "Mozambique",
	MM: "Myanmar",
	NA: "Namibia",
	NR: "Nauru",
	NP: "Nepal",
	NL: "Netherlands",
	AN: "Netherlands Antilles",
	NC: "New Caledonia",
	NZ: "New Zealand",
	NI: "Nicaragua",
	NE: "Niger",
	NG: "Nigeria",
	NU: "Niue",
	NF: "Norfolk Island",
	MP: "Northern Mariana Islands",
	NO: "Norway",
	OM: "Oman",
	PK: "Pakistan",
	PW: "Palau",
	PS: "Palestina",
	PA: "Panama",
	PG: "Papua New Guinea",
	PY: "Paraguay",
	PE: "Peru",
	PH: "Philippines",
	PN: "Pitcairn",
	PL: "Poland",
	PT: "Portugal",
	PR: "Puerto Rico",
	QA: "Qatar",
	RE: "Reunion",
	RO: "Romania",
	RU: "Russia",
	RQ: "Russian Federation",
	RW: "Rwanda",
	SH: "Saint Helena",
	KN: "Saint Kitts and Nevis",
	LC: "Saint Lucia",
	PM: "Saint Pierre and Miquelon",
	VC: "Saint Vincent and the Grenadines",
	WS: "Samoa",
	SM: "San Marino",
	ST: "Sao Tome and Principe",
	SA: "Saudi Arabia",
	SN: "Senegal",
	RS: "Serbia",
	CS: "Servi N Montenegro",
	SC: "Seychelles",
	SL: "Sierra Leone",
	SG: "Singapore",
	SX: "Sint Maarten",
	SK: "Slovakia",
	SI: "Slovenia",
	SB: "Solomon Islands",
	SO: "Somalia",
	ZA: "South Africa",
	GS: "South Georgia and the South Sandwich Islands",
	SS: "South Sudan",
	ES: "Spain",
	LK: "Sri Lanka",
	SD: "Sudan",
	SR: "Suriname",
	SJ: "Svalbard and Jan Mayen Islands",
	SZ: "Swaziland",
	SE: "Sweden",
	CH: "Switzerland",
	SY: "Syria",
	TW: "Taiwan",
	TJ: "Tajikistan",
	TZ: "Tanzania",
	TH: "Thailand",
	TL: "Timor Leste",
	TG: "Togo",
	TK: "Tokelau",
	TO: "Tonga",
	TT: "Trinidad and Tobago",
	TN: "Tunisia",
	TR: "Turkey",
	TM: "Turkmenistan",
	TC: "Turks and Caicos Islands",
	TV: "Tuvalu",
	UG: "Uganda",
	UA: "Ukraine",
	AE: "United Arab Emirates",
	US: "United States",
	UM: "United States Minor Outlying Islands",
	UY: "Uruguay",
	UZ: "Uzbekistan",
	VU: "Vanuatu",
	VA: "Vatican City State",
	VE: "Venezuela",
	VN: "Vietnam",
	VG: "Virgin Islands British",
	VI: "Virgin Islands Us",
	WF: "Wallis and Futuna Islands",
	EH: "Western Sahara",
	YE: "Yemen",
	ZR: "Zaire",
	ZM: "Zambia",
	ZW: "Zimbabwe",
};

export const IDENTIFICATION_TYPES_MAP: Record<string, string> = {
	"051": "Osobna iskaznica (domaća)",
	"028": "Osobna putovnica (domaća)",
	"103": "Vozačka dozvola (domaća)",
	"027": "Osobna iskaznica (strana)",
	"002": "Osobna putovnica (strana)",
	"032": "Vozačka dozvola (strana)",
	"003": "Obiteljska putovnica (strana)",
	"004": "Zajednička putovnica (strana)",
	"005": "Dječja putovnica (strana)",
	"006": "Diplomatska putovnica (strana)",
	"007": "Službena putovnica (strana)",
	"008": "Pomorska knjižica (strana)",
	"009": "Brodarska knjižica (strana)",
	"011": "Putni list za strance",
	"012": "Putna isprava za izbjeglice",
	"013": "Putna isprava za osobe bez državljanstva",
	"022": "Potvrda o oduzimanju ili zadržavanju putne isprave",
	"023": "Potvrda o prijavi gubitka ili nestanka putne isprave",
	"024": "Osobna iskaznica za stranca - izdana od RH",
	"025": "Posebna osobna iskaznica - diplomatska konzularna službena",
	"026": "Isprave izdane na temelju međunarodnih ugovora",
	"029": "Pogranična propusnica (strana)",
	"030": "Izbjeglički karton - izdan od RH",
	"033": "Dječja putovnica (domaća)",
	"034": "Putni list za strance (strani)",
	"035": "Zajednička putovnica (domaća)",
	"036": "Diplomatska putovnica (domaća)",
	"037": "Službena putovnica (domaća)",
	"038": "Pomorska iskaznica (domaća)",
	"039": "Ostalo",
	"052": "Obiteljska putovnica (domaća)",
	"999": "Zdravstvena iskaznica",
};

export function getIdentificationTypesLabel(code: string): string {
	return IDENTIFICATION_TYPES_MAP[code] ?? code;
}

/**
 * Get nationality label from code.
 */
export function getNationalityLabel(code: string): string {
	return NATIONALITIES_MAP[code] ?? code; // fallback to code if unknown
}

/**
 * Get country label from code.
 */
export function getCountryLabel(code: string): string {
	return COUNTRIES_MAP[code] ?? code;
}

const intlRegionByLocale = new Map<string, Intl.DisplayNames>();

function intlRegionsFor(locale: string): Intl.DisplayNames {
	const loc = locale?.trim() || "en";
	if (!intlRegionByLocale.has(loc)) {
		try {
			intlRegionByLocale.set(loc, new Intl.DisplayNames([loc], { type: "region" }));
		} catch {
			intlRegionByLocale.set(loc, new Intl.DisplayNames(["en"], { type: "region" }));
		}
	}
	return intlRegionByLocale.get(loc)!;
}

/**
 * Localized **country/territory name** for a LOV code (ISO 3166-1 alpha-2 or alpha-3 as returned by Opera).
 * Uses {@link Intl.DisplayNames} — not nationality demonyms (“Croatian”), but region names (“Croatia” / “Hrvatska”).
 */
export function getLocalizedRegionLabelFromLovCode(code: string, locale: string): string {
	const c = (code ?? "").trim().toUpperCase();
	if (!c) return "";

	let alpha2: string | undefined;
	if (c.length === 2) {
		alpha2 = c;
	} else if (c.length === 3) {
		alpha2 = countries.alpha3ToAlpha2(c) ?? undefined;
		if (!alpha2 && (c === "XKX" || c === "XXK")) {
			alpha2 = "XK";
		}
	} else {
		return code;
	}

	if (!alpha2) {
		return getNationalityLabel(c) || getCountryLabel(c) || c;
	}

	try {
		const name = intlRegionsFor(locale).of(alpha2);
		if (name) return name;
	} catch {
		/* ignore */
	}

	return getCountryLabel(alpha2) || getNationalityLabel(c) || c;
}

/**
 * OWS {@code mapLovResponseToMap} returns {@code camelCase(Opera LOV description) -> lov value}
 * (e.g. {@code china -> CHN}). Normalize to {@code lovCode -> lovCode} so the map encodes “allowed codes”;
 * localized labels are resolved in the UI via {@link getLocalizedRegionLabelFromLovCode}.
 *
 * @see com.mikos.onlinecheckin.ows.guest.mapper.GuestMapper#mapLovResponseToMap
 */
export function normalizeNationalitiesLovMap(raw: Record<string, string>): Record<string, string> {
	const out: Record<string, string> = {};
	for (const code of Object.values(raw)) {
		const c = code?.trim();
		if (!c) continue;
		out[c] = c;
	}
	return out;
}

/**
 * Backend `/titles` returns `{ camelCase(description) → Opera LOV value }`
 * (e.g. `{ "mr": "Mr", "prof": "Prof." }`) because {@code GuestMapper#mapLovResponseToMap}
 * uses the camelCased description as the key. Opera's {@code UpdateName} expects the LOV
 * **value** to be sent back, so collapse to `{ value → value }` — the same shape used for
 * countries/nationalities. The value is also what we display in the dropdown (Opera typically
 * configures it as a presentable string like {@code "Mr"} or {@code "Prof."}).
 *
 * @see com.mikos.onlinecheckin.ows.guest.mapper.GuestMapper#mapLovResponseToMap
 */
export function normalizeTitlesLovMap(raw: Record<string, string>): Record<string, string> {
	const out: Record<string, string> = {};
	for (const value of Object.values(raw ?? {})) {
		const v = value?.trim();
		if (!v) continue;
		out[v] = v;
	}
	return out;
}

/** Same as {@link normalizeNationalitiesLovMap} for country LOVs; drops legacy Yugoslavia {@code YU}. */
export function normalizeCountriesLovMap(raw: Record<string, string>): Record<string, string> {
	const out: Record<string, string> = {};
	for (const code of Object.values(raw)) {
		const c = code?.trim();
		if (!c || c === "YU") continue;
		out[c] = c;
	}
	return out;
}

/** Sort `[code, label]` pairs by display label (A–Z), using locale-aware collation when `locale` is set. */
export function sortCodeLabelEntriesByLabel(
	entries: [string, string][],
	locale?: string
): [string, string][] {
	return [...entries].sort((a, b) =>
		a[1].localeCompare(b[1], locale || undefined, { sensitivity: "base" })
	);
}
